import { BoxV2 as Box, Button, Flex, TextInput } from 'portal-commons';
import { FunctionComponent, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { faPhone, faRocketLaunch } from '@fortawesome/pro-regular-svg-icons';

import { useControllers } from './hooks';
import { LaunchDetailsFormValues } from './types';
import FormSection from '../FormSection';
import { UseCase } from '@/apis/core';
import { DRAFT_RCS_CAMPAIGN_ENABLED } from '@/constants';

interface LaunchDetailsFormProps {
  useCase: UseCase;
  defaultValues?: LaunchDetailsFormValues;
  onChange?: (values: LaunchDetailsFormValues) => void;
  onPrevious?: () => void;
  onSave?: () => void;
  onNext?: () => void;
}

const Container: FunctionComponent = ({ children }) => (
  <Flex sx={{ flexDirection: 'column', gap: '30px', width: '100%' }}>
    {children}
  </Flex>
);

const Subtitle: FunctionComponent = ({ children }) => (
  <Box
    sx={{
      color: '#333E41',
      fontWeight: 400,
      fontSize: '16px',
      marginBottom: '16px',
    }}
  >
    {children}
  </Box>
);

const UseCaseContainer: FunctionComponent = ({ children }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'auto auto',
      columnGap: '32px',
      rowGap: '12px',
    }}
  >
    {children}
  </Box>
);

const UrlContainer: FunctionComponent = ({ children }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'auto auto',
      columnGap: '32px',
      rowGap: '12px',
      marginTop: '40px',
    }}
  >
    {children}
  </Box>
);

const ContactContainer: FunctionComponent = ({ children }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'auto auto',
      columnGap: '32px',
      rowGap: '24px',
    }}
  >
    {children}
  </Box>
);

const ActionContainer: FunctionComponent = ({ children }) => (
  <Flex
    sx={{
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '9px',
      width: '100%',
      marginTop: '10px',
    }}
  >
    {children}
  </Flex>
);

const LaunchDetailsForm: FunctionComponent<LaunchDetailsFormProps> = ({
  useCase,
  defaultValues,
  onChange,
  onPrevious,
  onSave,
  onNext,
}) => {
  const {
    control,
    formState: { isSubmitting, errors },
    handleSubmit,
    watch,
  } = useForm<LaunchDetailsFormValues>({
    defaultValues,
  });
  const {
    actionTriggerMessages,
    setActionTriggerMessages,
    validateActionTriggerMessages,
    actionTriggerMessagesError,
    messageTypes,
    setMessageTypes,
    validateMessageTypes,
    messageTypesError,
    optinFlow,
    setOptinFlow,
    validateOptinFlow,
    optinFlowError,
    optoutFlow,
    setOptoutFlow,
    validateOptoutFlow,
    optoutFlowError,
    screenshotUrl,
    setScreenshotUrl,
    validateScreenshotUrl,
    screenshotUrlError,
    videoUrl,
    setVideoUrl,
    validateVideoUrl,
    videoUrlError,
    technicalContactName,
    setTechnicalContactName,
    validateTechnicalContactName,
    technicalContactNameError,
    technicalContactJobTitle,
    setTechnicalContactJobTitle,
    validateTechnicalContactJobTitle,
    technicalContactJobTitleError,
    technicalContactEmail,
    setTechnicalContactEmail,
    validateTechnicalContactEmail,
    technicalContactEmailError,
  } = useControllers(control);

  const handleValid = () => {
    onNext?.();
  };

  useEffect(() => {
    const { unsubscribe } = watch((values) => {
      onChange?.(values as LaunchDetailsFormValues);
    });
    return () => unsubscribe();
  }, [watch]);

  return (
    <form noValidate onSubmit={handleSubmit(handleValid)}>
      <Container>
        <FormSection
          icon={faRocketLaunch}
          title="Launch Details"
          message="Enter the launch details for your RCS campaign."
        >
          <Flex sx={{ flexDirection: 'column' }}>
            <Subtitle>
              <Box as="span" sx={{ fontWeight: 600 }}>
                Use-Case:
              </Box>
              {` ${useCase.displayName}`}
            </Subtitle>
            <UseCaseContainer>
              <TextInput
                multiline
                error={!!actionTriggerMessagesError?.message}
                label="What actions will trigger messages?"
                note={actionTriggerMessagesError?.message}
                maxLength={2000}
                minRows={3}
                maxRows={3}
                value={actionTriggerMessages}
                onChange={setActionTriggerMessages}
                onBlur={validateActionTriggerMessages}
                data-testid="actionTriggerMessages"
              />
              <TextInput
                multiline
                error={!!messageTypesError?.message}
                label="What type of messages do you send?"
                note={messageTypesError?.message}
                maxLength={2000}
                minRows={3}
                maxRows={3}
                value={messageTypes}
                onChange={setMessageTypes}
                onBlur={validateMessageTypes}
                data-testid="messageTypes"
              />
              <TextInput
                multiline
                error={!!optinFlowError?.message}
                label="How do you obtain Opt-In?"
                note={optinFlowError?.message}
                maxLength={2000}
                minRows={3}
                maxRows={3}
                value={optinFlow}
                onChange={setOptinFlow}
                onBlur={validateOptinFlow}
                data-testid="optinFlow"
              />
              <TextInput
                multiline
                error={!!optoutFlowError?.message}
                label="What messages does the user send to Opt-Out?"
                note={optoutFlowError?.message}
                maxLength={2000}
                minRows={3}
                maxRows={3}
                value={optoutFlow}
                onChange={setOptoutFlow}
                onBlur={validateOptoutFlow}
                data-testid="optoutFlow"
              />
            </UseCaseContainer>
            <UrlContainer>
              <TextInput
                error={!!screenshotUrlError?.message}
                label="Screenshot URL (Optional)"
                note={screenshotUrlError?.message}
                maxLength={100}
                value={screenshotUrl}
                onChange={setScreenshotUrl}
                onBlur={validateScreenshotUrl}
                data-testid="screenshotUrl"
              />
              <TextInput
                error={!!videoUrlError?.message}
                label="Video URL (Optional)"
                note={videoUrlError?.message}
                maxLength={100}
                value={videoUrl}
                onChange={setVideoUrl}
                onBlur={validateVideoUrl}
                data-testid="videoUrl"
              />
            </UrlContainer>
          </Flex>
        </FormSection>
        <FormSection
          icon={faPhone}
          title="Technical Contact"
          message="Enter the details for the person to contact if there are any technical issues with your agent."
        >
          <ContactContainer>
            <TextInput
              error={!!technicalContactNameError?.message}
              label="Name"
              note={technicalContactNameError?.message}
              maxLength={50}
              value={technicalContactName}
              onChange={setTechnicalContactName}
              onBlur={validateTechnicalContactName}
              data-testid="technicalContactName"
            />
            <TextInput
              error={!!technicalContactJobTitleError?.message}
              label="Job Title"
              note={technicalContactJobTitleError?.message}
              maxLength={50}
              value={technicalContactJobTitle}
              onChange={setTechnicalContactJobTitle}
              onBlur={validateTechnicalContactJobTitle}
              data-testid="technicalContactJobTitle"
            />
            <TextInput
              error={!!technicalContactEmailError?.message}
              label="Email"
              note={technicalContactEmailError?.message}
              maxLength={100}
              value={technicalContactEmail}
              onChange={setTechnicalContactEmail}
              onBlur={validateTechnicalContactEmail}
              data-testid="technicalContactEmail"
            />
          </ContactContainer>
        </FormSection>
        <ActionContainer>
          <Button variant="outline" onClick={onPrevious}>
            Previous
          </Button>
          {DRAFT_RCS_CAMPAIGN_ENABLED && (
            <Button variant="outline" onClick={onSave}>
              Save Draft
            </Button>
          )}
          <Button
            type="submit"
            disabled={
              isSubmitting ||
              Object.values(errors).some((error) => !!error?.message)
            }
          >
            Next
          </Button>
        </ActionContainer>
      </Container>
    </form>
  );
};

export default LaunchDetailsForm;
